import ExternalOption from '@/blocks/Scraper/components/ExternalOption';
import ScraperOption from '@/blocks/Scraper/components/ScraperOption';
import ScraperTemplateSelectorSectionHeader from '@/blocks/Scraper/components/ScraperTemplateSelectorSectionHeader';
import Tags from '@/blocks/Scraper/components/Tags';
import { Target, targets } from '@/blocks/Scraper/constants/targets';
import { Divider } from '@/components/atoms/Divider';
import { useTranslation } from '@/lib/i18n/i18n.client';
import { TARGET_GROUP } from '@decodo/utils';
import React from 'react';

type ScraperTemplateSelectorListContentProps = {
  selectedTag: TARGET_GROUP;
  setSelectedTag: (tag: TARGET_GROUP | '') => void;
  handleSelectTarget: (target: Target) => void;
};

const ScraperTemplateSelectorListContent = ({
  selectedTag,
  setSelectedTag,
  handleSelectTarget,
}: ScraperTemplateSelectorListContentProps) => {
  const { texts } = useTranslation();
  const filteredTargets = React.useMemo(() => {
    const availableOptions: Target[] = [];
    const unavailableOptions: Target[] = [];

    for (const target of targets) {
      if (selectedTag && target.group !== selectedTag) {
        continue;
      }

      if (target.unavailable) {
        unavailableOptions.push(target);
      } else {
        availableOptions.push(target);
      }
    }

    return { availableOptions, unavailableOptions };
  }, [selectedTag]);

  const { availableOptions, unavailableOptions } = filteredTargets;

  return (
    <div className='p-6 dark:bg-gray-dark-950 rounded'>
      <Tags
        selectedTag={selectedTag}
        setSelectedTag={(tag: string) => {
          if (tag === selectedTag) {
            setSelectedTag('');
            return;
          }

          setSelectedTag(tag as TARGET_GROUP);
        }}
      />
      <div className='overflow-y-auto max-h-[350px]'>
        {availableOptions.length > 0 && (
          <>
            <ScraperTemplateSelectorSectionHeader>
              {texts.scraper.scraping_templates}
            </ScraperTemplateSelectorSectionHeader>

            <Divider
              showOnDark
              classNames={{ root: 'dark:border-gray-dark-700' }}
            />
            {availableOptions.map((target) => (
              <ScraperOption
                key={target.name}
                target={target}
                handleSelect={() => handleSelectTarget(target)}
              />
            ))}
          </>
        )}

        {unavailableOptions.length > 0 && (
          <>
            <ScraperTemplateSelectorSectionHeader>
              {texts.scraper.more_templates_available_in_dashboard}
            </ScraperTemplateSelectorSectionHeader>
            <div className='h-[1px] bg-gradient-main-90deg' />
            {unavailableOptions.map((target) => (
              <ExternalOption key={target.name} target={target} />
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default ScraperTemplateSelectorListContent;
